import React, { useState, useEffect } from 'react';
import './App.css';
import { Header } from './components/Header';
import { TabButtons } from './components/TabButtons';
import { UserSection } from './components/UserSection';
import { Tablero } from './components/Tablero';
import { Resumen } from './components/Resumen';
import { Footer } from './components/Footer';
import { getISOMonth, monthKey, uid, parseCsv } from './utils';
import { loadMonth, saveMonth, generateResumen, editTask } from './services';

function App() {

  // Inicialización de constantes
  const NOW = new Date();
  const { year: TY, month: TM } = getISOMonth(NOW);

  // Estado global
  const [curYear, setCurYear] = useState(TY);
  const [curMonth, setCurMonth] = useState(TM);
  const [userName, setUserName] = useState(localStorage.getItem("dgit_nombre") || "");
  const [semana, setSemana] = useState({ tareas: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [tab, setTab] = useState("tablero");
  const [expandedTask, setExpandedTask] = useState(null);
  const [showAddTarea, setShowAddTarea] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [nuevaTarea, setNuevaTarea] = useState({ titulo: "", descripcion: "", responsable: "", proyecto: "", jira: "", prioridad: "normal" });
  const [avanceInputs, setAvanceInputs] = useState({});
  const [avanceEstados, setAvanceEstados] = useState({});
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);
  const [showImportDocx, setShowImportDocx] = useState(false);
  const [docxPreview, setDocxPreview] = useState(null);
  const [resumen, setResumen] = useState("");
  const [resumenLoading, setResumenLoading] = useState(false);
  const [resumenErr, setResumenErr] = useState(null);
  const [copied, setCopied] = useState(false);

  // Cargar datos de la semana
  const loadSemana = async () => {
    setLoading(true);
    setSaveErr(null);
    try {
      const mk = monthKey(curYear, curMonth);
      const data = await loadMonth(mk);
      setSemana(data);
    } catch (e) {
      setSaveErr("Error al cargar: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando cambien año/mes
  useEffect(() => {
    loadSemana();
    setExpandedTask(null);
    setResumen("");
  }, [curYear, curMonth]);

  // Navegar meses
  const navMonth = (delta) => {
    let nextMonth = curMonth + delta;
    let nextYear = curYear;
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    } else if (nextMonth < 1) {
      nextMonth = 12;
      nextYear -= 1;
    }
    setCurYear(nextYear);
    setCurMonth(nextMonth);
  };

  const goToday = () => {
    const { year, month } = getISOMonth(new Date());
    setCurYear(year);
    setCurMonth(month);
  };

  // Guardar datos en backend
  const saveSemana = async (mutateFn) => {
    setSaving(true);
    setSaveErr(null);
    try {
      const mk = monthKey(curYear, curMonth);
      const updated = mutateFn(semana || { tareas: [] });
      const saved = await saveMonth(mk, updated);
      setSemana(saved);
      return true;
    } catch (e) {
      setSaveErr("Error al guardar: " + e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Usuario
  const guardarNombre = (nombre) => {
    localStorage.setItem("dgit_nombre", nombre);
    setUserName(nombre);
  };

  const cambiarNombre = () => {
    localStorage.removeItem("dgit_nombre");
    setUserName("");
  };

  // Tareas
  const agregarTarea = async () => {
    if (!nuevaTarea.titulo.trim() || !nuevaTarea.responsable.trim()) return;
    const tarea = {
      id: uid(),
      titulo: nuevaTarea.titulo.trim(),
      descripcion: (nuevaTarea.descripcion || "").trim(),
      responsable: nuevaTarea.responsable.trim(),
      proyecto: nuevaTarea.proyecto.trim(),
      jira: nuevaTarea.jira.trim(),
      prioridad: nuevaTarea.prioridad || "normal",
      avances: []
    };
    await saveSemana(d => ({ ...d, tareas: [...(d.tareas || []), tarea] }));
    setNuevaTarea({ titulo: "", descripcion: "", responsable: "", proyecto: "", jira: "", prioridad: "normal" });
    setShowAddTarea(false);
  };

  const eliminarTarea = async (id) => {
    if (!window.confirm("¿Eliminar esta tarea?")) return;
    await saveSemana(d => ({ ...d, tareas: (d.tareas || []).filter(t => t.id !== id) }));
    if (expandedTask === id) setExpandedTask(null);
  };

  const editarTarea = async (id, cambios) => {
    setSaving(true);
    setSaveErr(null);
    try {
      const mk = monthKey(curYear, curMonth);
      await editTask(mk, id, cambios);
      setSemana(prev => ({
        ...prev,
        tareas: (prev.tareas || []).map(t => t.id === id ? { ...t, ...cambios, dirty: true } : t)
      }));
      return true;
    } catch (e) {
      setSaveErr("Error al editar: " + e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Avances
  const agregarAvanceHandler = async (tareaId) => {
    const texto = (avanceInputs[tareaId] || "").trim();
    const estado = avanceEstados[tareaId] || "Avanzando";
    if (!texto || !userName) return;
    const av = { id: uid(), fecha: new Date().toISOString(), autor: userName, texto, estado };
    await saveSemana(d => ({
      ...d,
      tareas: (d.tareas || []).map(t =>
        t.id === tareaId ? { ...t, avances: [...(t.avances || []), av], dirty: true } : t
      )
    }));
    setAvanceInputs({ ...avanceInputs, [tareaId]: "" });
    setAvanceEstados({ ...avanceEstados, [tareaId]: "Avanzando" });
  };

  const editarAvanceHandler = async (tareaId, avanceId, nuevosCambios) => {
    await saveSemana(d => ({
      ...d,
      tareas: (d.tareas || []).map(t =>
        t.id === tareaId
          ? {
              ...t,
              avances: (t.avances || []).map(av =>
                av.id === avanceId ? { ...av, ...nuevosCambios } : av
              ),
              dirty: true
            }
          : t
      )
    }));
  };

  const eliminarAvanceHandler = async (tareaId, avanceId) => {
    if (!window.confirm("¿Eliminar esta sub-tarea (avance)?")) return;
    await saveSemana(d => ({
      ...d,
      tareas: (d.tareas || []).map(t =>
        t.id === tareaId
          ? {
              ...t,
              avances: (t.avances || []).filter(av => av.id !== avanceId),
              dirty: true
            }
          : t
      )
    }));
  };

  const moverAvanceHandler = async (sourceTaskId, targetTaskId, avanceId) => {
    await saveSemana(d => {
      const sourceTask = (d.tareas || []).find(t => t.id === sourceTaskId);
      const targetTask = (d.tareas || []).find(t => t.id === targetTaskId);
      if (!sourceTask || !targetTask) return d;
      const avanceToMove = (sourceTask.avances || []).find(av => av.id === avanceId);
      if (!avanceToMove) return d;

      return {
        ...d,
        tareas: (d.tareas || []).map(t => {
          if (t.id === sourceTaskId) {
            return {
              ...t,
              avances: (t.avances || []).filter(av => av.id !== avanceId),
              dirty: true
            };
          }
          if (t.id === targetTaskId) {
            return {
              ...t,
              avances: [...(t.avances || []), { ...avanceToMove, fecha: new Date().toISOString() }],
              dirty: true
            };
          }
          return t;
        })
      };
    });
  };

  // CSV
  const handleCsvTextChange = (text) => {
    setCsvText(text);
    setCsvPreview(parseCsv(text));
  };

  const confirmarImport = async () => {
    if (!csvPreview?.length) return;
    await saveSemana(d => ({ ...d, tareas: [...(d.tareas || []), ...csvPreview] }));
    setCsvPreview(null);
    setCsvText("");
    setShowImport(false);
  };

  const confirmarImportDocx = async () => {
    if (!docxPreview?.length) return;
    await saveSemana(d => ({ ...d, tareas: [...(d.tareas || []), ...docxPreview] }));
    setDocxPreview(null);
    setShowImportDocx(false);
  };

  const generarResumen = async () => {
    setResumenLoading(true);
    setResumenErr(null);
    setResumen("");
    try {
      const mk = monthKey(curYear, curMonth);
      const data = await generateResumen(mk, semana.tareas || []);
      setResumen(data.resumen || "");
    } catch (e) {
      setResumenErr(e.message || "Error al generar el resumen");
    } finally {
      setResumenLoading(false);
    }
  };

  const handleCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isCurrent = curYear === TY && curMonth === TM;
  const wk = monthKey(curYear, curMonth);

  return (
    <div>
      <Header
        year={curYear}
        month={curMonth}
        onPrevMonth={() => navMonth(-1)}
        onNextMonth={() => navMonth(1)}
        onToday={goToday}
        isCurrent={isCurrent}
      />
      <UserSection
        userName={userName}
        onSaveName={guardarNombre}
        onChangeName={cambiarNombre}
      />
      <TabButtons
        activeTab={tab}
        onTabChange={setTab}
      />

      {tab === "tablero" ? (
        <Tablero
          tareas={semana.tareas || []}
          loading={loading}
          saving={saving}
          userName={userName}
          onAddAvance={agregarAvanceHandler}
          onDeleteTarea={eliminarTarea}
          onEditTarea={editarTarea}
          onEditAvance={editarAvanceHandler}
          onDeleteAvance={eliminarAvanceHandler}
          onMoveAvance={moverAvanceHandler}
          onToggleAddTarea={() => setShowAddTarea(!showAddTarea)}
          showAddTarea={showAddTarea}
          nuevaTarea={nuevaTarea}
          onNuevaTareaChange={(key, value) => setNuevaTarea({ ...nuevaTarea, [key]: value })}
          onAgregarTarea={agregarTarea}
          onToggleImport={() => setShowImport(!showImport)}
          showImport={showImport}
          csvText={csvText}
          onCsvTextChange={handleCsvTextChange}
          csvPreview={csvPreview}
          onConfirmarImport={confirmarImport}
          onToggleImportDocx={() => setShowImportDocx(!showImportDocx)}
          showImportDocx={showImportDocx}
          docxPreview={docxPreview}
          onDocxPreviewChange={setDocxPreview}
          onConfirmarImportDocx={confirmarImportDocx}
          onLoadSemana={loadSemana}
          onRefresh={loadSemana}
          avanceInputs={avanceInputs}
          avanceEstados={avanceEstados}
          onAvanceInputChange={(tareaId, value) => setAvanceInputs({ ...avanceInputs, [tareaId]: value })}
          onAvanceEstadoChange={(tareaId, value) => setAvanceEstados({ ...avanceEstados, [tareaId]: value })}
          expandedTask={expandedTask}
          onToggleExpand={(tareaId) => setExpandedTask(expandedTask === tareaId ? null : tareaId)}
        />
      ) : (
        <Resumen
          weekKey={wk}
          tareas={semana.tareas || []}
          resumen={resumen}
          resumenLoading={resumenLoading}
          resumenErr={resumenErr}
          copied={copied}
          onGenerarResumen={generarResumen}
          onCopied={handleCopied}
        />
      )}

      <Footer weekKey={wk} />
    </div>
  );
}

export default App;
